/**
 * Shared Configuration Types and Schemas
 *
 * This module contains all configuration-related types and Zod schemas
 * shared between the main application (config.ts) and the configuration
 * update utility (update-config.ts).
 *
 * By centralizing these types, we ensure consistency and avoid duplication
 * between the React application and the Deno configuration script.
 */

import { z } from "zod";

// =============================================================================
// CONFIGURATION SCHEMAS
// =============================================================================

/**
 * App configuration schema
 * Defines basic application metadata and environment settings
 */
export const AppConfigSchema = z.object({
	name: z.string().min(1),
	version: z.string().min(1),
	environment: z.enum(["local", "development", "qa", "production"]),
	title: z.string().min(1),
});

/**
 * API configuration schema
 * Defines API endpoints, timeouts, and connection settings
 */
export const ApiConfigSchema = z.object({
	baseUrl: z.string().url(),
	timeout: z.number().positive(),
	retryAttempts: z.number().min(0).max(10),
	debug: z.boolean().optional(),
	endpoints: z.object({
		auth: z.string(),
		users: z.string(),
		admin: z.string(),
		health: z.string(),
	}),
});

/**
 * Feature flags schema
 * Defines boolean flags for enabling/disabling application features
 */
export const FlagsSchema = z.object({
	debugMode: z.boolean(),
	mockApi: z.boolean(),
	devTools: z.boolean(),
	authentication: z.boolean(),
	adminPanel: z.boolean(),
	userProfiles: z.boolean(),
	notifications: z.boolean(),
	analytics: z.boolean(),
	betaFeatures: z.boolean(),
	darkMode: z.boolean(),
	multiLanguage: z.boolean(),
	allowDarkMode: z.boolean(),
	/** Test draw / edit geometry control on workspace maps. */
	mapGeometryEditTools: z.boolean().default(true),
	/**
	 * Mapbox DEM terrain + building extrusions on workspace maps.
	 * Off: pitch control only resets to 2D when tilted (disabled at pitch 0).
	 * On: full 2D ↔ 3D pitch toggle + terrain/buildings.
	 */
	map3dBasemap: z.boolean().default(false),
	/**
	 * MapMvtDevtools panel on Destiny MapShell (org overview + workspace maps).
	 * Off by default — enable in `public/config.json` flags when debugging MVT tiles.
	 */
	mapMvtDevtools: z.boolean().default(false),
	/**
	 * Add-from-text button and modal on the Tickets list.
	 * Off unless the environment sets this flag in public/config.json.
	 */
	ticketFromText: z.boolean().default(false),
});

/**
 * Backward compatibility alias for FlagsSchema
 * @deprecated Use FlagsSchema instead
 */
export const FeaturesSchema = FlagsSchema;

/**
 * UI configuration schema
 * Defines visual appearance and branding settings
 */
export const UiConfigSchema = z.object({
	theme: z.enum(["light", "dark", "auto"]),
	primaryColor: z.string(),
	brandLogo: z.string(),
	favicon: z.string(),
});

/**
 * Datadog RUM configuration schema
 * Defines Real User Monitoring settings for observability
 */
export const DatadogRumConfigSchema = z.object({
	enabled: z.boolean(),
	applicationId: z.string(),
	clientToken: z.string(),
	site: z.string(),
	service: z.string(),
	env: z.string(),
	version: z.string(),
	sessionSampleRate: z.number().min(0).max(100),
	sessionReplaySampleRate: z.number().min(0).max(100),
	trackUserInteractions: z.boolean(),
	trackResources: z.boolean(),
	trackLongTasks: z.boolean(),
	defaultPrivacyLevel: z.enum(["allow", "mask", "mask-user-input"]),
});

/**
 * Geocode rate limit configuration schema
 */
export const GeocodeRateLimitConfigSchema = z.object({
	limit: z.number().int().positive(),
	windowMs: z.number().int().positive(),
	windowType: z.enum(["sliding", "fixed"]),
});

/**
 * Geocode suggest / resolve configuration schema
 */
export const GeocodeConfigSchema = z.object({
	debounceMs: z.number().int().positive(),
	minQueryLength: z.number().int().min(1),
	maxSuggestions: z.number().int().positive(),
	countryCode: z.string().min(1),
	suggestCacheStaleTimeMs: z.number().int().nonnegative(),
	suggestCacheGcTimeMs: z.number().int().nonnegative(),
	geocodeCacheStaleTimeMs: z.number().int().nonnegative(),
	geocodeCacheGcTimeMs: z.number().int().nonnegative(),
	suggestRateLimit: GeocodeRateLimitConfigSchema,
	geocodeRateLimit: GeocodeRateLimitConfigSchema,
});

/**
 * Default geocode configuration (Set Location suggest + resolve)
 */
export const DEFAULT_GEOCODE_CONFIG = {
	debounceMs: 400,
	minQueryLength: 3,
	maxSuggestions: 5,
	countryCode: "USA",
	suggestCacheStaleTimeMs: 30_000,
	suggestCacheGcTimeMs: 5 * 60_000,
	geocodeCacheStaleTimeMs: 5 * 60_000,
	geocodeCacheGcTimeMs: 30 * 60_000,
	suggestRateLimit: {
		limit: 20,
		windowMs: 60_000,
		windowType: "sliding" as const,
	},
	geocodeRateLimit: {
		limit: 10,
		windowMs: 60_000,
		windowType: "sliding" as const,
	},
};

/**
 * Integrations configuration schema
 * Defines third-party service integrations and their settings
 */
export const DEFAULT_MIXPANEL_API_HOST = "https://api.mixpanel.com";

export const DEFAULT_MIXPANEL_CONFIG = {
	enabled: false,
	projectToken: "",
	apiHost: DEFAULT_MIXPANEL_API_HOST,
};

export const MixpanelConfigSchema = z
	.object({
		enabled: z.boolean(),
		projectToken: z.string(),
		apiHost: z.string(),
	})
	.default(DEFAULT_MIXPANEL_CONFIG);

export const IntegrationsSchema = z.object({
	mapbox: z.object({
		publicAccessToken: z.string(),
	}),
	mixpanel: MixpanelConfigSchema,
	geocode: GeocodeConfigSchema.default(DEFAULT_GEOCODE_CONFIG),
	analytics: z.object({
		enabled: z.boolean(),
		trackingId: z.string(),
	}),
	monitoring: z.object({
		enabled: z.boolean(),
		apiKey: z.string(),
	}),
	datadog: z
		.object({
			rum: DatadogRumConfigSchema.optional(),
		})
		.optional(),
});

/**
 * Partial Integrations configuration schema for updates
 * Allows partial updates at any depth - all fields are optional
 * Used by update-config.ts to allow updating specific integration fields
 * without requiring all other fields to be provided
 */
export const PartialIntegrationsSchema = z
	.object({
		mapbox: z
			.object({
				publicAccessToken: z.string().optional(),
			})
			.partial()
			.optional(),
		mixpanel: z
			.object({
				enabled: z.boolean().optional(),
				projectToken: z.string().optional(),
				apiHost: z.string().optional(),
			})
			.partial()
			.optional(),
		analytics: z
			.object({
				enabled: z.boolean().optional(),
				trackingId: z.string().optional(),
			})
			.partial()
			.optional(),
		monitoring: z
			.object({
				enabled: z.boolean().optional(),
				apiKey: z.string().optional(),
			})
			.partial()
			.optional(),
		datadog: z
			.object({
				rum: z
					.object({
						enabled: z.boolean().optional(),
						applicationId: z.string().optional(),
						clientToken: z.string().optional(),
						site: z.string().optional(),
						service: z.string().optional(),
						env: z.string().optional(),
						version: z.string().optional(),
						sessionSampleRate: z.number().min(0).max(100).optional(),
						sessionReplaySampleRate: z.number().min(0).max(100).optional(),
						trackUserInteractions: z.boolean().optional(),
						trackResources: z.boolean().optional(),
						trackLongTasks: z.boolean().optional(),
						defaultPrivacyLevel: z
							.enum(["allow", "mask", "mask-user-input"])
							.optional(),
					})
					.partial()
					.optional(),
			})
			.partial()
			.optional(),
		geocode: GeocodeConfigSchema.partial().optional(),
	})
	.partial();

/**
 * Security configuration schema
 * Defines security policies, CORS settings, and session management
 */
export const SecuritySchema = z.object({
	corsOrigins: z.array(z.string()),
	allowedDomains: z.array(z.string()),
	sessionTimeout: z.number().positive(),
	inactivityWarning: z.number().positive(),
	inactivityLogout: z.number().positive(),
	inactivityEnabled: z.boolean(),
});

/**
 * Environment-specific configuration schema
 * Combines all configuration sections for a specific environment
 */
export const EnvironmentConfigSchema = z.object({
	api: ApiConfigSchema,
	flags: FlagsSchema,
	ui: UiConfigSchema,
	integrations: IntegrationsSchema,
	security: SecuritySchema,
});

/**
 * Metadata schema
 * Tracks configuration file metadata and versioning
 */
export const MetadataSchema = z.object({
	lastUpdated: z.string(),
	updatedBy: z.string(),
	version: z.string(),
	schema: z.string(),
});

/**
 * Complete runtime configuration schema
 * The root schema that defines the entire configuration structure
 */
export const RuntimeConfigSchema = z.object({
	app: AppConfigSchema,
	environments: z.object({
		local: EnvironmentConfigSchema,
		development: EnvironmentConfigSchema,
		qa: EnvironmentConfigSchema,
		production: EnvironmentConfigSchema,
	}),
	_metadata: MetadataSchema,
});

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

/**
 * Inferred types from schemas for type-safe usage
 */
export type AppConfig = z.infer<typeof AppConfigSchema>;
export type ApiConfig = z.infer<typeof ApiConfigSchema>;
export type Flags = z.infer<typeof FlagsSchema>;
export type Features = z.infer<typeof FeaturesSchema>; // Backward compatibility
export type UiConfig = z.infer<typeof UiConfigSchema>;
export type DatadogRumConfig = z.infer<typeof DatadogRumConfigSchema>;
export type GeocodeRateLimitConfig = z.infer<
	typeof GeocodeRateLimitConfigSchema
>;
export type GeocodeConfig = z.infer<typeof GeocodeConfigSchema>;
export type Integrations = z.infer<typeof IntegrationsSchema>;
export type SecurityConfig = z.infer<typeof SecuritySchema>;
export type EnvironmentConfig = z.infer<typeof EnvironmentConfigSchema>;
export type RuntimeConfig = z.infer<typeof RuntimeConfigSchema>;

/**
 * Additional utility types
 */
export type EnvironmentName = keyof RuntimeConfig["environments"];
export type Config = RuntimeConfig; // Alias for backward compatibility

// =============================================================================
// PARTIAL TYPES FOR UPDATES
// =============================================================================

/**
 * Partial types for configuration updates
 * Used by the update utility to allow partial configuration changes
 */
export type PartialAppConfig = Partial<AppConfig>;
export type PartialApiConfig = Partial<ApiConfig>;
export type PartialFlagsConfig = Partial<Flags>;
export type PartialUiConfig = Partial<UiConfig>;

export type PartialIntegrationsConfig = {
	mapbox?: Partial<Integrations["mapbox"]>;
	mixpanel?: Partial<Integrations["mixpanel"]>;
	geocode?: Partial<GeocodeConfig>;
	analytics?: Partial<Integrations["analytics"]>;
	monitoring?: Partial<Integrations["monitoring"]>;
	datadog?: {
		rum?: Partial<DatadogRumConfig>;
	};
};

export type PartialEnvironmentConfig = {
	api?: PartialApiConfig;
	flags?: PartialFlagsConfig;
	ui?: PartialUiConfig;
	integrations?: PartialIntegrationsConfig;
	security?: Partial<SecurityConfig>;
};

export type ConfigUpdates = {
	app?: PartialAppConfig;
	environments?: {
		[K in EnvironmentName]?: PartialEnvironmentConfig;
	};
};

// =============================================================================
// RESOLVED CONFIG INTERFACE
// =============================================================================

/**
 * Interface for resolved configuration with computed values
 * Used by the main application after configuration processing
 */
export interface ResolvedConfig {
	app: AppConfig;
	api: ApiConfig;
	flags: Flags;
	ui: UiConfig;
	integrations: Integrations;
	security: SecurityConfig;
	_metadata: {
		lastUpdated: string;
		updatedBy: string;
		version: string;
		schema: string;
		resolvedAt: string;
		environment: EnvironmentName;
	};
}

// =============================================================================
// VALIDATION HELPERS
// =============================================================================

/**
 * Validates a complete runtime configuration
 */
export function validateRuntimeConfig(config: unknown): RuntimeConfig {
	return RuntimeConfigSchema.parse(config);
}

/**
 * Validates a partial configuration update
 */
export function validateConfigUpdates(updates: unknown): ConfigUpdates {
	const ConfigUpdatesSchema = z.object({
		app: AppConfigSchema.partial().optional(),
		environments: z
			.object({
				local: EnvironmentConfigSchema.partial().optional(),
				development: EnvironmentConfigSchema.partial().optional(),
				qa: EnvironmentConfigSchema.partial().optional(),
				production: EnvironmentConfigSchema.partial().optional(),
			})
			.partial()
			.optional(),
	});

	return ConfigUpdatesSchema.parse(updates);
}

/**
 * Type guard to check if a value is a valid environment name
 */
export function isValidEnvironmentName(env: string): env is EnvironmentName {
	return ["local", "development", "qa", "production"].includes(env);
}

// =============================================================================
// DEFAULT CONFIGURATION VALUES
// =============================================================================

/**
 * Default API configuration values
 */
export const DEFAULT_API_CONFIG: ApiConfig = {
	baseUrl: "http://localhost:5096",
	timeout: 3000000,
	retryAttempts: 3,
	debug: false,
	endpoints: {
		auth: "/authentication",
		users: "/users",
		admin: "/admin",
		health: "/health",
	},
};

/**
 * Default feature flags configuration
 */
export const DEFAULT_FLAGS_CONFIG: Flags = {
	debugMode: false,
	mockApi: true,
	devTools: true,
	authentication: true,
	adminPanel: true,
	userProfiles: true,
	notifications: false,
	analytics: false,
	betaFeatures: false,
	darkMode: true,
	multiLanguage: false,
	allowDarkMode: true,
	mapGeometryEditTools: true,
	map3dBasemap: false,
	mapMvtDevtools: false,
	ticketFromText: false,
};

/**
 * Default UI configuration
 */
export const DEFAULT_UI_CONFIG: UiConfig = {
	theme: "auto",
	primaryColor: "#3b82f6",
	brandLogo: "/logo.svg",
	favicon: "/favicon.ico",
};

/**
 * Default Datadog RUM configuration
 */
export const DEFAULT_DATADOG_RUM_CONFIG: DatadogRumConfig = {
	enabled: false,
	applicationId: "",
	clientToken: "",
	site: "us5.datadoghq.com",
	service: "fawkes-web",
	env: "local",
	version: "1.0.0",
	sessionSampleRate: 100,
	sessionReplaySampleRate: 100,
	trackUserInteractions: true,
	trackResources: true,
	trackLongTasks: true,
	defaultPrivacyLevel: "mask-user-input",
};

/**
 * Default integrations configuration
 */
export const DEFAULT_INTEGRATIONS_CONFIG: Integrations = {
	mapbox: {
		publicAccessToken: "",
	},
	mixpanel: { ...DEFAULT_MIXPANEL_CONFIG },
	geocode: DEFAULT_GEOCODE_CONFIG,
	analytics: {
		enabled: false,
		trackingId: "",
	},
	monitoring: {
		enabled: false,
		apiKey: "",
	},
	datadog: {
		rum: { ...DEFAULT_DATADOG_RUM_CONFIG },
	},
};

/**
 * Default security configuration
 */
export const DEFAULT_SECURITY_CONFIG: SecurityConfig = {
	corsOrigins: ["http://localhost:3000"],
	allowedDomains: ["localhost"],
	sessionTimeout: 3600000,
	inactivityWarning: 1500000,
	inactivityLogout: 1800000,
	inactivityEnabled: true,
};

/**
 * Default app configuration
 */
export const DEFAULT_APP_CONFIG: AppConfig = {
	name: "Fawkes Web",
	version: "1.0.0",
	environment: "local",
	title: "Fawkes Web Application",
};

/**
 * Environment-specific default overrides
 */
export const ENVIRONMENT_DEFAULTS: Record<
	EnvironmentName,
	Partial<EnvironmentConfig>
> = {
	local: {
		api: {
			...DEFAULT_API_CONFIG,
			baseUrl: "http://localhost:5096",
			debug: true,
		},
		flags: {
			...DEFAULT_FLAGS_CONFIG,
			debugMode: true,
			mockApi: true,
			analytics: false,
			mapGeometryEditTools: true,
		},
		integrations: {
			...DEFAULT_INTEGRATIONS_CONFIG,
			mapbox: {
				publicAccessToken: "",
			},
			analytics: {
				enabled: false,
				trackingId: "",
			},
		},
	},
	development: {
		api: {
			...DEFAULT_API_CONFIG,
			baseUrl: "https://api.fawkes.dev.example.com",
			debug: true,
		},
		flags: {
			...DEFAULT_FLAGS_CONFIG,
			debugMode: false,
			mockApi: false,
			analytics: true,
			mapGeometryEditTools: true,
		},
		integrations: {
			...DEFAULT_INTEGRATIONS_CONFIG,
			mapbox: {
				publicAccessToken: "",
			},
			analytics: {
				enabled: true,
				trackingId: "DEV-ANALYTICS-ID",
			},
		},
	},
	qa: {
		api: {
			...DEFAULT_API_CONFIG,
			baseUrl: "https://api.fawkes.qa.example.com",
			debug: false,
		},
		flags: {
			...DEFAULT_FLAGS_CONFIG,
			debugMode: false,
			mockApi: false,
			analytics: true,
			betaFeatures: true,
		},
		integrations: {
			...DEFAULT_INTEGRATIONS_CONFIG,
			mapbox: {
				publicAccessToken: "",
			},
			analytics: {
				enabled: true,
				trackingId: "QA-ANALYTICS-ID",
			},
		},
	},
	production: {
		api: {
			...DEFAULT_API_CONFIG,
			baseUrl: "https://api.fawkes.example.com",
			debug: false,
		},
		flags: {
			...DEFAULT_FLAGS_CONFIG,
			debugMode: false,
			mockApi: false,
			devTools: false,
			analytics: true,
		},
		integrations: {
			...DEFAULT_INTEGRATIONS_CONFIG,
			mapbox: {
				publicAccessToken: "",
			},
			analytics: {
				enabled: true,
				trackingId: "PROD-ANALYTICS-ID",
			},
		},
	},
};

// =============================================================================
// DEFAULT CONFIGURATION FACTORY FUNCTIONS
// =============================================================================

/**
 * Create default environment configuration for a specific environment
 */
export function createDefaultEnvironmentConfig(
	env: EnvironmentName = "local",
): EnvironmentConfig {
	const baseConfig: EnvironmentConfig = {
		api: { ...DEFAULT_API_CONFIG },
		flags: { ...DEFAULT_FLAGS_CONFIG },
		ui: { ...DEFAULT_UI_CONFIG },
		integrations: { ...DEFAULT_INTEGRATIONS_CONFIG },
		security: { ...DEFAULT_SECURITY_CONFIG },
	};

	// Apply environment-specific overrides
	const envOverrides = ENVIRONMENT_DEFAULTS[env];
	if (envOverrides) {
		return {
			api: { ...baseConfig.api, ...envOverrides.api },
			flags: { ...baseConfig.flags, ...envOverrides.flags },
			ui: { ...baseConfig.ui, ...envOverrides.ui },
			integrations: {
				...baseConfig.integrations,
				...envOverrides.integrations,
			},
			security: { ...baseConfig.security, ...envOverrides.security },
		};
	}

	return baseConfig;
}

/**
 * Create a complete default runtime configuration
 */
export function createDefaultRuntimeConfig(): RuntimeConfig {
	return {
		app: { ...DEFAULT_APP_CONFIG },
		environments: {
			local: createDefaultEnvironmentConfig("local"),
			development: createDefaultEnvironmentConfig("development"),
			qa: createDefaultEnvironmentConfig("qa"),
			production: createDefaultEnvironmentConfig("production"),
		},
		_metadata: {
			lastUpdated: new Date().toISOString(),
			updatedBy: "system",
			version: "1.0.0",
			schema: "1.0",
		},
	};
}
